import isDate from 'validator/es/lib/isDate';
import isISO8601 from 'validator/es/lib/isISO8601';
import parseNumericValue, { zeroPad } from '@/utils/numberHelper';
import { localToUTCDate, localToUTCDatetime } from '@/utils/dateHelper';
import { dictionaryDateTypes } from '@/constants/fields';

const dateString = (date) => (
  `${date.getFullYear()}-${zeroPad((date.getMonth() + 1), 2)}-${zeroPad(date.getDate(), 2)}`
);

const timeString = (date) => (
  `${zeroPad(date.getHours(), 2)}:${zeroPad(date.getMinutes(), 2)}:${zeroPad(date.getSeconds(), 2)}`
);

const hasTimePart = (string) => /:/.test(string);

const dateOnly = (string) => isDate(string, { format: 'YYYY-MM-DD', delimiters: ['-'], strictMode: true });

export const parseDateToDateString = (date) => (date ? dateString(date) : null);

export const parseDateToDateTimeString = (date) => (date ? `${dateString(date)}:${timeString(date)}` : null);

export const parseDateToTimeTzString = (date) => (date ? `${timeString(date)}.000Z` : null);

export const isDateString = (string) => {
  if (typeof string !== 'string' || parseNumericValue(string)) {
    return false;
  }

  const parsed = Date.parse(string);
  return !Number.isNaN(parsed);
};

export const isTimeString = (string) => {
  if (typeof string !== 'string') {
    return false;
  }

  if (isDateString(string) && hasTimePart(string)) {
    return true;
  }

  const date = new Date().toISOString();
  const isoDateString = date.split('T')[0];
  return isDateString(`${isoDateString}T${string}`);
};

const isISOFormat = (string) => isISO8601(string, { strict: true, strictSeparator: true });

export const dateTimePickerFormat = (fieldType) => {
  if (fieldType === dictionaryDateTypes.DATETIME) {
    return 'dd/MMM/yyyy hh:mm aa';
  }
  if (fieldType === dictionaryDateTypes.TIME) {
    return 'hh:mm:ss aa';
  }

  return 'dd/MMM/yyyy';
};

export const cohortDateValue = (value, defaultValue = null) => {
  if (!value || !isDateString(value)) {
    return defaultValue;
  }

  return localToUTCDate(new Date(value));
};

export const cohortDateTimeValue = (value, defaultValue = null) => {
  if (!value || !isDateString(value)) {
    return defaultValue;
  }

  if (isISOFormat(value) || dateOnly(value)) {
    return localToUTCDatetime(new Date(value));
  }

  return new Date(value);
};

export const cohortTimeValue = (value, defaultValue = null) => {
  if (!value || !isTimeString(value)) {
    return defaultValue;
  }

  const today = new Date();

  const todayDate = today.toISOString().split('T')[0];
  const parsedDatetime = Date.parse(`${todayDate}T${value}`);

  if (!Number.isNaN(parsedDatetime)) {
    const datetime = new Date(`${todayDate}T${value}`);
    return new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      datetime.getUTCHours(),
      datetime.getUTCMinutes(),
      datetime.getUTCSeconds(),
    );
  }

  if (isISOFormat(value) || dateOnly(value)) {
    return localToUTCDatetime(new Date(value));
  }

  const dateValue = new Date(value);

  return new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
    dateValue.getHours(),
    dateValue.getMinutes(),
    dateValue.getSeconds(),
  );
};
